import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  MapPin,
  Search,
  RefreshCw,
  Plus,
  X,
  Check,
  Building2,
  UserCog,
  Users,
  Trash2,
  Edit,
  Phone,
  Clock,
  CheckCircle,
  XCircle,
  Navigation,
  Circle,
  Loader2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  LogIn,
} from "lucide-react";
import { MapContainer, TileLayer, Marker, useMapEvents, Circle as LeafletCircle, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Map click handler component
const MapClickHandler = ({ onLocationSelect }) => {
  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

// Component to programmatically fly to a location
const FlyToLocation = ({ position, zoom }) => {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, zoom || 12, { duration: 1.5 });
    }
  }, [position, zoom, map]);
  return null;
};
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export const CityManagement = () => {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [cities, setCities] = useState([]);
  const [cityAdmins, setCityAdmins] = useState([]);
  const [pendingCityAdmins, setPendingCityAdmins] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCity, setSelectedCity] = useState("all");
  const [activeTab, setActiveTab] = useState("cities");
  const [impersonating, setImpersonating] = useState(false);
  
  // Dialogs
  const [showCityDialog, setShowCityDialog] = useState(false);
  const [showCityAdminDialog, setShowCityAdminDialog] = useState(false);
  const [showAssignDriverDialog, setShowAssignDriverDialog] = useState(false);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  
  // Form data
  const [cityForm, setCityForm] = useState({ 
    name: "", 
    district: "",
    state: "", 
    country: "India", 
    is_active: true,
    latitude: null,
    longitude: null,
    service_radius_km: 50,
    manager: ""
  });
  const [cityAdminForm, setCityAdminForm] = useState({ name: "", phone: "", city_id: "" });
  const [editingCity, setEditingCity] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [assignCityId, setAssignCityId] = useState("");
  const mapRef = useRef(null);
  
  // Location search state
  const [locationSearch, setLocationSearch] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [flyToPosition, setFlyToPosition] = useState(null);
  
  // Pending approval state
  const [selectedPendingAdmin, setSelectedPendingAdmin] = useState(null);
  const [approveCityId, setApproveCityId] = useState("");
  const [processingApproval, setProcessingApproval] = useState(false);
  
  // City search, sort, and filter state
  const [citySearch, setCitySearch] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");
  const [citySortBy, setCitySortBy] = useState("name"); // name, district, state, drivers
  const [citySortOrder, setCitySortOrder] = useState("asc"); // asc, desc

  // Get unique states and districts from cities for filter dropdowns
  const uniqueStates = [...new Set(cities.map(c => c.state).filter(Boolean))].sort();
  const uniqueDistricts = [...new Set(cities.map(c => c.district).filter(Boolean))].sort();
  
  // Filter and sort cities
  const filteredCities = cities
    .filter(city => {
      const matchesSearch = !citySearch || 
        city.name?.toLowerCase().includes(citySearch.toLowerCase());
      const matchesState = !stateFilter || city.state === stateFilter;
      const matchesDistrict = !districtFilter || city.district === districtFilter;
      return matchesSearch && matchesState && matchesDistrict;
    })
    .sort((a, b) => {
      let comparison = 0;
      if (citySortBy === "name") {
        comparison = (a.name || "").localeCompare(b.name || "");
      } else if (citySortBy === "district") {
        comparison = (a.district || "").localeCompare(b.district || "");
      } else if (citySortBy === "state") {
        comparison = (a.state || "").localeCompare(b.state || "");
      } else if (citySortBy === "drivers") {
        comparison = (a.driver_count || 0) - (b.driver_count || 0);
      } else if (citySortBy === "admins") {
        comparison = (a.city_admin_count || 0) - (b.city_admin_count || 0);
      }
      return citySortOrder === "asc" ? comparison : -comparison;
    });

  useEffect(() => {
    if (user?.id) {
      fetchData();
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchDriversByCity();
    }
  }, [selectedCity, user?.id]);

  const fetchData = async () => {
    if (!user?.id) return;
    try {
      const [citiesRes, cityAdminsRes, pendingRes] = await Promise.all([
        axios.get(`${API}/cities?admin_id=${user.id}`),
        axios.get(`${API}/admin/city-admins?admin_id=${user.id}`),
        axios.get(`${API}/admin/city-admins/pending?admin_id=${user.id}`)
      ]);
      // Sort cities alphabetically
      const sortedCities = (citiesRes.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(sortedCities);
      setCityAdmins(cityAdminsRes.data);
      setPendingCityAdmins(pendingRes.data);
    } catch (err) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const fetchDriversByCity = async () => {
    try {
      const params = selectedCity !== "all" ? `?city_id=${selectedCity}` : "";
      const res = await axios.get(`${API}/admin/${user.id}/drivers-by-city${params}`);
      setDrivers(res.data);
    } catch (err) {
      toast.error("Failed to load drivers");
    }
  };

  // City CRUD
  const handleCreateCity = async () => {
    try {
      if (editingCity) {
        await axios.put(`${API}/admin/cities/${editingCity.id}?admin_id=${user.id}`, cityForm);
        toast.success("City updated");
      } else {
        await axios.post(`${API}/admin/cities?admin_id=${user.id}`, cityForm);
        toast.success("City created");
      }
      setShowCityDialog(false);
      setCityForm({ 
        name: "", 
        state: "", 
        country: "India", 
        is_active: true,
        latitude: null,
        longitude: null,
        service_radius_km: 50
      });
      setEditingCity(null);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save city");
    }
  };

  const handleDeleteCity = async (cityId) => {
    if (!confirm("Are you sure you want to delete this city?")) return;
    try {
      await axios.delete(`${API}/admin/cities/${cityId}?admin_id=${user.id}`);
      toast.success("City deleted");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete city");
    }
  };

  const openEditCity = (city) => {
    setEditingCity(city);
    setCityForm({
      name: city.name,
      district: city.district || "",
      state: city.state,
      country: city.country || "India",
      is_active: city.is_active,
      latitude: city.latitude || null,
      longitude: city.longitude || null,
      service_radius_km: city.service_radius_km || 50,
      manager: city.manager || ""
    });
    setLocationSearch("");
    setSearchResults([]);
    setFlyToPosition(city.latitude && city.longitude ? [city.latitude, city.longitude] : null);
    setShowCityDialog(true);
  };

  // Location search using OpenStreetMap Nominatim API
  const searchLocation = async () => {
    if (!locationSearch.trim()) return;
    
    setIsSearching(true);
    setSearchResults([]);
    
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationSearch)}&limit=5&countrycodes=in`,
        { headers: { 'Accept-Language': 'en' } }
      );
      
      if (response.data && response.data.length > 0) {
        setSearchResults(response.data);
      } else {
        toast.error("No locations found. Try a different search term.");
      }
    } catch (err) {
      toast.error("Failed to search location. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const selectSearchResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    
    setCityForm({ ...cityForm, latitude: lat, longitude: lng });
    setFlyToPosition([lat, lng]);
    setSearchResults([]);
    setLocationSearch("");
    toast.success(`Location set to ${result.display_name.split(',')[0]}`);
  };

  // City Admin CRUD
  const handleCreateCityAdmin = async () => {
    try {
      await axios.post(`${API}/admin/city-admins?admin_id=${user.id}`, cityAdminForm);
      toast.success("City Admin created");
      setShowCityAdminDialog(false);
      setCityAdminForm({ name: "", phone: "", city_id: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create city admin");
    }
  };

  const handleDeleteCityAdmin = async (cityAdminId) => {
    if (!confirm("Are you sure you want to delete this city admin?")) return;
    try {
      await axios.delete(`${API}/admin/city-admins/${cityAdminId}?admin_id=${user.id}`);
      toast.success("City Admin deleted");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete city admin");
    }
  };

  // Driver City Assignment
  const handleAssignDriverCity = async () => {
    if (!selectedDriver || !assignCityId) return;
    try {
      await axios.put(`${API}/admin/driver/${selectedDriver.id}/city?city_id=${assignCityId}&admin_id=${user.id}`);
      toast.success("Driver assigned to city");
      setShowAssignDriverDialog(false);
      setSelectedDriver(null);
      setAssignCityId("");
      fetchDriversByCity();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to assign driver");
    }
  };

  const handleRemoveDriverCity = async (driverId) => {
    try {
      await axios.delete(`${API}/admin/driver/${driverId}/city?admin_id=${user.id}`);
      toast.success("Driver city assignment removed");
      fetchDriversByCity();
    } catch (err) {
      toast.error("Failed to remove city assignment");
    }
  };

  // Pending City Admin Approval/Rejection
  const handleApproveAdmin = async () => {
    if (!selectedPendingAdmin || !approveCityId) {
      toast.error("Please select a city to assign");
      return;
    }
    setProcessingApproval(true);
    try {
      await axios.put(`${API}/admin/city-admins/${selectedPendingAdmin.id}/approve?city_id=${approveCityId}&admin_id=${user.id}`);
      toast.success(`${selectedPendingAdmin.name} approved and assigned to city`);
      setShowApproveDialog(false);
      setSelectedPendingAdmin(null);
      setApproveCityId("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve city admin");
    } finally {
      setProcessingApproval(false);
    }
  };

  const handleRejectAdmin = async (adminId, adminName) => {
    if (!confirm(`Are you sure you want to reject ${adminName}'s request?`)) return;
    try {
      await axios.put(`${API}/admin/city-admins/${adminId}/reject?admin_id=${user.id}`);
      toast.success("City Admin request rejected");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject city admin");
    }
  };

  // Login As City Admin
  const handleLoginAsCityAdmin = async (cityAdmin) => {
    if (impersonating) return;
    setImpersonating(true);
    
    try {
      const res = await axios.post(`${API}/admin/${user.id}/login-as-city-admin/${cityAdmin.id}`);
      
      // Store current admin session before switching
      const currentToken = localStorage.getItem("biketaxi_token");
      localStorage.setItem("original_admin_user", JSON.stringify(user));
      localStorage.setItem("original_admin_token", currentToken);
      localStorage.setItem("is_impersonating", "true");
      
      // Login as the city admin
      login(res.data.token, res.data.user);
      toast.success(`Now logged in as ${cityAdmin.name}`);
      
      // Navigate to city admin dashboard
      navigate("/city-admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to login as city admin");
    } finally {
      setImpersonating(false);
    }
  };

  // Login As Driver
  const handleLoginAsDriver = async (driver) => {
    if (impersonating) return;
    setImpersonating(true);
    
    try {
      const res = await axios.post(`${API}/admin/${user.id}/login-as-driver/${driver.id}`);
      
      // Store current admin session before switching
      const currentToken = localStorage.getItem("biketaxi_token");
      localStorage.setItem("original_admin_user", JSON.stringify(user));
      localStorage.setItem("original_admin_token", currentToken);
      localStorage.setItem("is_impersonating", "true");
      
      // Login as the driver
      login(res.data.token, res.data.user);
      toast.success(`Now logged in as ${driver.name}`);
      
      // Navigate to driver dashboard
      navigate("/driver");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to login as driver");
    } finally {
      setImpersonating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="city-management">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold">City Management</h2>
          <p className="text-muted-foreground">
            Manage cities, city admins, and driver assignments
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="cities" className="flex items-center gap-2">
            <MapPin className="w-4 h-4" /> Cities ({cities.length})
          </TabsTrigger>
          <TabsTrigger value="pending" className="flex items-center gap-2 relative" data-testid="pending-approvals-tab">
            <Clock className="w-4 h-4" /> Pending
            {pendingCityAdmins.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {pendingCityAdmins.length}
              </span>
            )}
          </TabsTrigger>
          <TabsTrigger value="city-admins" className="flex items-center gap-2">
            <UserCog className="w-4 h-4" /> City Admins ({cityAdmins.length})
          </TabsTrigger>
          <TabsTrigger value="drivers" className="flex items-center gap-2">
            <Users className="w-4 h-4" /> Driver Mapping
          </TabsTrigger>
        </TabsList>

        {/* Cities Tab */}
        <TabsContent value="cities">
          <div className="flex flex-col gap-4 mb-4">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold">All Cities ({filteredCities.length})</h3>
              <Button onClick={() => { 
                setEditingCity(null); 
                setCityForm({ 
                  name: "", 
                  district: "",
                  state: "", 
                  country: "India", 
                  is_active: true,
                  latitude: null,
                  longitude: null,
                  service_radius_km: 50
                });
                setLocationSearch("");
                setSearchResults([]);
                setFlyToPosition(null);
                setShowCityDialog(true); 
              }}>
                <Plus className="w-4 h-4 mr-2" /> Add City
              </Button>
            </div>
            
            {/* Search, Filter, and Sort Controls */}
            <div className="flex flex-wrap gap-3 items-center">
              {/* Search by city name */}
              <div className="relative flex-1 min-w-[200px]">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Search by city name..."
                  value={citySearch}
                  onChange={(e) => setCitySearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              
              {/* Filter by state */}
              <div className="flex items-center gap-2">
                <Filter className="w-4 h-4 text-muted-foreground" />
                <select
                  value={stateFilter}
                  onChange={(e) => setStateFilter(e.target.value)}
                  className="px-3 py-2 border rounded-md bg-background text-sm min-w-[150px]"
                >
                  <option value="">All States</option>
                  {uniqueStates.map(state => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              
              {/* Filter by district */}
              {uniqueDistricts.length > 0 && (
                <select
                  value={districtFilter}
                  onChange={(e) => setDistrictFilter(e.target.value)}
                  className="px-3 py-2 border rounded-md bg-background text-sm min-w-[150px]"
                >
                  <option value="">All Districts</option>
                  {uniqueDistricts.map(district => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              )}
              
              {/* Sort controls */}
              <div className="flex items-center gap-2">
                <ArrowUpDown className="w-4 h-4 text-muted-foreground" />
                <select
                  value={citySortBy}
                  onChange={(e) => setCitySortBy(e.target.value)}
                  className="px-3 py-2 border rounded-md bg-background text-sm"
                >
                  <option value="name">Sort by Name</option>
                  <option value="district">Sort by District</option>
                  <option value="state">Sort by State</option>
                  <option value="drivers">Sort by Drivers</option>
                  <option value="admins">Sort by Admins</option>
                </select>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => setCitySortOrder(prev => prev === "asc" ? "desc" : "asc")}
                  title={citySortOrder === "asc" ? "Ascending" : "Descending"}
                >
                  {citySortOrder === "asc" ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
                </Button>
              </div>
              
              {/* Clear filters */}
              {(citySearch || stateFilter || districtFilter) && (
                <Button 
                  variant="ghost" 
                  size="sm"
                  onClick={() => {
                    setCitySearch("");
                    setStateFilter("");
                    setDistrictFilter("");
                  }}
                >
                  <X className="w-4 h-4 mr-1" /> Clear
                </Button>
              )}
            </div>
          </div>
          
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      if (citySortBy === "name") {
                        setCitySortOrder(prev => prev === "asc" ? "desc" : "asc");
                      } else {
                        setCitySortBy("name");
                        setCitySortOrder("asc");
                      }
                    }}
                  >
                    <div className="flex items-center gap-1">
                      City Name
                      {citySortBy === "name" && (
                        citySortOrder === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      if (citySortBy === "district") {
                        setCitySortOrder(prev => prev === "asc" ? "desc" : "asc");
                      } else {
                        setCitySortBy("district");
                        setCitySortOrder("asc");
                      }
                    }}
                  >
                    <div className="flex items-center gap-1">
                      District
                      {citySortBy === "district" && (
                        citySortOrder === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      if (citySortBy === "state") {
                        setCitySortOrder(prev => prev === "asc" ? "desc" : "asc");
                      } else {
                        setCitySortBy("state");
                        setCitySortOrder("asc");
                      }
                    }}
                  >
                    <div className="flex items-center gap-1">
                      State
                      {citySortBy === "state" && (
                        citySortOrder === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Country</TableHead>
                  <TableHead>Coordinates</TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      if (citySortBy === "admins") {
                        setCitySortOrder(prev => prev === "asc" ? "desc" : "asc");
                      } else {
                        setCitySortBy("admins");
                        setCitySortOrder("desc");
                      }
                    }}
                  >
                    <div className="flex items-center gap-1">
                      City Admins
                      {citySortBy === "admins" && (
                        citySortOrder === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => {
                      if (citySortBy === "drivers") {
                        setCitySortOrder(prev => prev === "asc" ? "desc" : "asc");
                      } else {
                        setCitySortBy("drivers");
                        setCitySortOrder("desc");
                      }
                    }}
                  >
                    <div className="flex items-center gap-1">
                      Drivers
                      {citySortBy === "drivers" && (
                        citySortOrder === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                      )}
                    </div>
                  </TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Manager</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredCities.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} className="text-center py-8 text-muted-foreground">
                      {cities.length === 0 ? "No cities found. Add your first city!" : "No cities match your search/filter criteria."}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCities.map((city) => (
                  <TableRow key={city.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4 text-primary" />
                        {city.name}
                      </div>
                    </TableCell>
                    <TableCell>{city.district || '-'}</TableCell>
                    <TableCell>{city.state}</TableCell>
                    <TableCell>{city.country}</TableCell>
                    <TableCell>
                      {city.latitude && city.longitude ? (
                        <div className="text-xs">
                          <span className="text-green-600 font-medium">Set</span>
                          <span className="text-muted-foreground ml-1">
                            ({city.service_radius_km || 50}km)
                          </span>
                        </div>
                      ) : (
                        <span className="text-xs text-amber-600">Not set</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">
                        {cityAdmins.filter(ca => ca.assigned_city_id === city.id).length} admins
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                        {drivers.filter(d => d.assigned_city_id === city.id).length} drivers
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${city.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {city.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm text-muted-foreground">
                        {city.manager || '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={() => openEditCity(city)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteCity(city.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Pending Approvals Tab */}
        <TabsContent value="pending" data-testid="pending-approvals-content">
          <div className="mb-4">
            <h3 className="text-lg font-semibold">Pending City Admin Requests</h3>
            <p className="text-sm text-muted-foreground">
              Review and approve or reject city admin signup requests
            </p>
          </div>

          {pendingCityAdmins.length === 0 ? (
            <div className="text-center py-12 border rounded-lg bg-muted/30">
              <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
              <p className="text-muted-foreground">No pending approval requests</p>
              <p className="text-sm text-muted-foreground mt-1">
                All city admin requests have been processed
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {pendingCityAdmins.map((admin) => (
                <div 
                  key={admin.id} 
                  className="p-4 bg-card rounded-xl border border-border flex flex-col md:flex-row md:items-center justify-between gap-4"
                  data-testid={`pending-admin-${admin.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center">
                      <Clock className="w-6 h-6 text-amber-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-foreground">{admin.name}</h4>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Phone className="w-3 h-3" />
                        {admin.phone}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Requested: {admin.created_at ? new Date(admin.created_at).toLocaleString() : 'Unknown'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 ml-16 md:ml-0">
                    <Button 
                      variant="outline"
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => handleRejectAdmin(admin.id, admin.name)}
                      data-testid={`reject-admin-${admin.id}`}
                    >
                      <XCircle className="w-4 h-4 mr-2" />
                      Reject
                    </Button>
                    <Button 
                      onClick={() => {
                        setSelectedPendingAdmin(admin);
                        setApproveCityId("");
                        setShowApproveDialog(true);
                      }}
                      data-testid={`approve-admin-${admin.id}`}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Approve
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* City Admins Tab */}
        <TabsContent value="city-admins">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">City Admins</h3>
            <Button onClick={() => setShowCityAdminDialog(true)} disabled={cities.length === 0}>
              <Plus className="w-4 h-4 mr-2" /> Add City Admin
            </Button>
          </div>
          
          {cities.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Create a city first before adding city admins.
            </div>
          ) : (
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Assigned City</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {cityAdmins.map((ca) => (
                    <TableRow key={ca.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <UserCog className="w-4 h-4 text-blue-500" />
                          {ca.name}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Phone className="w-3 h-3" />
                          {ca.phone}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                          {ca.assigned_city_name || 'Not assigned'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {ca.created_at ? new Date(ca.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="ghost" 
                            onClick={() => handleLoginAsCityAdmin(ca)}
                            disabled={impersonating}
                            title="Login as this City Admin"
                            data-testid={`login-as-city-admin-${ca.id}`}
                          >
                            <LogIn className="w-4 h-4 text-blue-500" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-500" onClick={() => handleDeleteCityAdmin(ca.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                  {cityAdmins.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                        No city admins found. Click "Add City Admin" to create one.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </TabsContent>

        {/* Driver Mapping Tab */}
        <TabsContent value="drivers">
          <div className="mb-4">
            <h3 className="text-lg font-semibold mb-2">Driver City Mapping</h3>
            <div className="flex gap-2 flex-wrap">
              <Button
                size="sm"
                variant={selectedCity === "all" ? "default" : "outline"}
                onClick={() => setSelectedCity("all")}
              >
                All Drivers
              </Button>
              {cities.map((city) => (
                <Button
                  key={city.id}
                  size="sm"
                  variant={selectedCity === city.id ? "default" : "outline"}
                  onClick={() => setSelectedCity(city.id)}
                >
                  {city.name} ({drivers.filter(d => d.assigned_city_id === city.id).length})
                </Button>
              ))}
            </div>
          </div>

          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Driver Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Assigned City</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drivers.map((driver) => (
                  <TableRow key={driver.id}>
                    <TableCell className="font-medium">{driver.name}</TableCell>
                    <TableCell>{driver.phone}</TableCell>
                    <TableCell>
                      <span className="capitalize">{driver.vehicle_type}</span>
                      {driver.vehicle_number && ` - ${driver.vehicle_number}`}
                    </TableCell>
                    <TableCell>
                      {driver.assigned_city_name ? (
                        <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                          {driver.assigned_city_name}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-sm">Not assigned</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        driver.is_online ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}>
                        {driver.is_online ? 'Online' : 'Offline'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleLoginAsDriver(driver)}
                          disabled={impersonating}
                          title="Login as this Driver"
                          data-testid={`login-as-driver-${driver.id}`}
                        >
                          <LogIn className="w-4 h-4 text-blue-500" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedDriver(driver);
                            setAssignCityId(driver.assigned_city_id || "");
                            setShowAssignDriverDialog(true);
                          }}
                        >
                          {driver.assigned_city_id ? 'Change' : 'Assign'}
                        </Button>
                        {driver.assigned_city_id && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-500"
                            onClick={() => handleRemoveDriverCity(driver.id)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {drivers.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No drivers found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Create/Edit City Dialog */}
      <Dialog open={showCityDialog} onOpenChange={setShowCityDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingCity ? 'Edit City' : 'Add New City'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>City Name</Label>
                <Input
                  placeholder="Enter city name"
                  value={cityForm.name}
                  onChange={(e) => setCityForm({ ...cityForm, name: e.target.value })}
                />
              </div>
              <div>
                <Label>District</Label>
                <Input
                  placeholder="Enter district"
                  value={cityForm.district}
                  onChange={(e) => setCityForm({ ...cityForm, district: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>State</Label>
                <Input
                  placeholder="Enter state"
                  value={cityForm.state}
                  onChange={(e) => setCityForm({ ...cityForm, state: e.target.value })}
                />
              </div>
              <div>
                <Label>Country</Label>
                <Input
                  placeholder="Enter country"
                  value={cityForm.country}
                  onChange={(e) => setCityForm({ ...cityForm, country: e.target.value })}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Service Radius (KM)</Label>
                <Input
                  type="number"
                  min="1"
                  max="500"
                  placeholder="50"
                  value={cityForm.service_radius_km || ""}
                  onChange={(e) => setCityForm({ ...cityForm, service_radius_km: parseFloat(e.target.value) || 50 })}
                />
              </div>
              <div className="flex items-center gap-2 pt-6">
                <Switch
                  checked={cityForm.is_active}
                  onCheckedChange={(checked) => setCityForm({ ...cityForm, is_active: checked })}
                />
                <Label>Active</Label>
              </div>
            </div>
            
            {/* Manager Field */}
            <div>
              <Label>Manager (for reporting)</Label>
              <Input
                placeholder="Enter manager name"
                value={cityForm.manager || ""}
                onChange={(e) => setCityForm({ ...cityForm, manager: e.target.value })}
              />
            </div>
            
            {/* City Center Location Picker */}
            <div className="border rounded-lg p-4 space-y-3 bg-muted/30">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Navigation className="w-4 h-4 text-primary" />
                  <Label className="font-semibold">City Center Location</Label>
                </div>
                {cityForm.latitude && cityForm.longitude && (
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      setCityForm({ ...cityForm, latitude: null, longitude: null });
                      setFlyToPosition(null);
                    }}
                  >
                    <X className="w-3 h-3 mr-1" /> Clear
                  </Button>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                Search for a city or click on the map to select the city center. This is used for fare calculation when pickup location text doesn't match.
              </p>
              
              {/* Location Search Box */}
              <div className="relative">
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Search city... (e.g., Bangalore, Mumbai)"
                      value={locationSearch}
                      onChange={(e) => setLocationSearch(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && searchLocation()}
                      className="pl-9"
                      data-testid="location-search-input"
                    />
                  </div>
                  <Button 
                    type="button" 
                    onClick={searchLocation}
                    disabled={isSearching || !locationSearch.trim()}
                    data-testid="location-search-btn"
                  >
                    {isSearching ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Search className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                
                {/* Search Results Dropdown */}
                {searchResults.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-background border rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="location-search-results">
                    {searchResults.map((result, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => selectSearchResult(result)}
                        className="w-full px-3 py-2 text-left text-sm hover:bg-muted flex items-start gap-2 border-b last:border-b-0"
                      >
                        <MapPin className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span className="line-clamp-2">{result.display_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-3 mb-2">
                <div>
                  <Label className="text-xs text-muted-foreground">Latitude</Label>
                  <Input
                    type="number"
                    step="0.000001"
                    placeholder="e.g., 12.9716"
                    value={cityForm.latitude || ""}
                    onChange={(e) => {
                      const val = e.target.value ? parseFloat(e.target.value) : null;
                      setCityForm({ ...cityForm, latitude: val });
                    }}
                    className="text-sm"
                  />
                </div>
                <div>
                  <Label className="text-xs text-muted-foreground">Longitude</Label>
                  <Input
                    type="number"
                    step="0.000001"
                    placeholder="e.g., 77.5946"
                    value={cityForm.longitude || ""}
                    onChange={(e) => {
                      const val = e.target.value ? parseFloat(e.target.value) : null;
                      setCityForm({ ...cityForm, longitude: val });
                    }}
                    className="text-sm"
                  />
                </div>
              </div>
              
              <div className="h-[280px] rounded-lg overflow-hidden border">
                <MapContainer
                  ref={mapRef}
                  center={[
                    cityForm.latitude || 20.5937, 
                    cityForm.longitude || 78.9629
                  ]}
                  zoom={cityForm.latitude ? 10 : 4}
                  style={{ height: "100%", width: "100%" }}
                  key={`${editingCity?.id || 'new'}-${showCityDialog}`}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <MapClickHandler 
                    onLocationSelect={(lat, lng) => {
                      setCityForm({ ...cityForm, latitude: lat, longitude: lng });
                      setFlyToPosition(null); // Reset fly position after manual click
                    }}
                  />
                  {flyToPosition && <FlyToLocation position={flyToPosition} zoom={12} />}
                  {cityForm.latitude && cityForm.longitude && (
                    <>
                      <Marker position={[cityForm.latitude, cityForm.longitude]} />
                      <LeafletCircle 
                        center={[cityForm.latitude, cityForm.longitude]}
                        radius={(cityForm.service_radius_km || 50) * 1000}
                        pathOptions={{ 
                          color: '#3b82f6', 
                          fillColor: '#3b82f6', 
                          fillOpacity: 0.1,
                          weight: 2
                        }}
                      />
                    </>
                  )}
                </MapContainer>
              </div>
              
              {cityForm.latitude && cityForm.longitude && (
                <div className="flex items-center gap-2 text-sm text-green-600 bg-green-50 px-3 py-2 rounded-lg">
                  <CheckCircle className="w-4 h-4" />
                  <span>
                    Location set: {cityForm.latitude.toFixed(4)}, {cityForm.longitude.toFixed(4)} 
                    (Service area: {cityForm.service_radius_km || 50}km radius)
                  </span>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCityDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateCity} disabled={!cityForm.name || !cityForm.state}>
              {editingCity ? 'Update' : 'Create'} City
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create City Admin Dialog */}
      <Dialog open={showCityAdminDialog} onOpenChange={setShowCityAdminDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add City Admin</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                placeholder="Enter name"
                value={cityAdminForm.name}
                onChange={(e) => setCityAdminForm({ ...cityAdminForm, name: e.target.value })}
              />
            </div>
            <div>
              <Label>Phone Number</Label>
              <Input
                placeholder="Enter phone number"
                value={cityAdminForm.phone}
                onChange={(e) => setCityAdminForm({ ...cityAdminForm, phone: e.target.value })}
              />
            </div>
            <div>
              <Label>Assign to City</Label>
              <Select value={cityAdminForm.city_id} onValueChange={(v) => setCityAdminForm({ ...cityAdminForm, city_id: v })}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a city" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map((city) => (
                    <SelectItem key={city.id} value={city.id}>{city.name}, {city.state}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCityAdminDialog(false)}>Cancel</Button>
            <Button 
              onClick={handleCreateCityAdmin} 
              disabled={!cityAdminForm.name || !cityAdminForm.phone || !cityAdminForm.city_id}
            >
              Create City Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Driver to City Dialog */}
      <Dialog open={showAssignDriverDialog} onOpenChange={setShowAssignDriverDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Driver to City</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p>
              Assign <strong>{selectedDriver?.name}</strong> to a city
            </p>
            <Select value={assignCityId} onValueChange={setAssignCityId}>
              <SelectTrigger>
                <SelectValue placeholder="Select a city" />
              </SelectTrigger>
              <SelectContent>
                {cities.map((city) => (
                  <SelectItem key={city.id} value={city.id}>{city.name}, {city.state}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAssignDriverDialog(false)}>Cancel</Button>
            <Button onClick={handleAssignDriverCity} disabled={!assignCityId}>
              Assign City
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve City Admin Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle>Approve City Admin</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-muted/50 rounded-lg">
              <p className="font-medium">{selectedPendingAdmin?.name}</p>
              <p className="text-sm text-muted-foreground">{selectedPendingAdmin?.phone}</p>
            </div>
            <div>
              <Label>Assign to City *</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Select a city for this City Admin to manage
              </p>
              <Select value={approveCityId} onValueChange={setApproveCityId}>
                <SelectTrigger data-testid="approve-city-select">
                  <SelectValue placeholder="Select a city" />
                </SelectTrigger>
                <SelectContent>
                  {cities.map((city) => (
                    <SelectItem key={city.id} value={city.id}>
                      {city.name}, {city.state}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {cities.length === 0 && (
              <p className="text-sm text-amber-600 bg-amber-50 p-3 rounded-lg">
                No cities available. Please create a city first before approving city admins.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowApproveDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleApproveAdmin} 
              disabled={!approveCityId || processingApproval || cities.length === 0}
              data-testid="confirm-approve-btn"
            >
              {processingApproval ? "Approving..." : "Approve & Assign"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
